import React, { Reducer, useCallback, useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AddCircle, PersonAdd, RemoveCircle } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, List, TextField } from '@mui/material';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { subDays, isBefore } from 'date-fns';
import dateFormat from 'dateformat';
import {
  useHotelDetailsQuery,
  useExpressCheckinConfirmationMutation,
  usePreRegBookingInfoLazyQuery,
} from '../generated/graphql';
import { ExpandMore } from '../Components/ExpandMore';
import { Footer } from '../Components/Footer';
import Loading from '../Components/Loading/Loading';
import { MainWrapper } from '../Components/MainWrapper';

interface FormProps {
  contact: any;
  fullname: string;
}

interface IAction<TType = string, TPayload = any> {
  type: TType;
  payload: TPayload;
}

const initialState: FormProps = { contact: [], fullname: '' };

const reducer: Reducer<FormProps, IAction> = (state, action) => {
  switch (action.type) {
    case 'reset':
      return initialState;
    default:
      return { ...state, [action.type]: action.payload };
  }
};

export default function Main() {
  const history = useNavigate();

  const queryParameters = new URLSearchParams(window.location.search);
  const URLBookingID = queryParameters.get('Booking') as string;
  const URLHotelID = queryParameters.get('Hotel') as string;

  const [contact, setContact] = useState<any[]>([]);
  const [guest, setGuest] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<any[]>([]);
  const [maxguest, setMaxGuest] = useState<number | undefined>(undefined);
  const [, dispatch] = useReducer(reducer, initialState);

  const {
    loading: HotelLoadings,
    data: { HotelDetails } = { HotelDetails: null },
  } = useHotelDetailsQuery({
    variables: { HotelID: URLHotelID },
    fetchPolicy: 'no-cache',
  });

  const [
    PreRegInfo,
    {
      loading: Loadings,
      data: { PreRegBookingInfo } = { PreRegBookingInfo: [] },
    },
  ] = usePreRegBookingInfoLazyQuery({ fetchPolicy: 'no-cache' });

  const [ExpressCheckinConfirmation] = useExpressCheckinConfirmationMutation({
    onCompleted: data => {
      if (data?.ExpressCheckinConfirmation === true) {
        history('/pre-checkin/view', {
          state: {
            BookingID: URLBookingID,
            HotelID: URLHotelID,
            Guest: guest[0]?.ContactInfo?.length,
            HotelDetails,
          },
        });
      }
    },
  });

  let dates: boolean | undefined;
  if (!HotelLoadings) {
    const NACutOffTime = new Date(HotelDetails?.NAProcessTime).getHours();
    const NATime = new Date().setHours(NACutOffTime);
    const TodayDate = new Date();
    const PreviousDate = subDays(new Date(), 1);
    const newDate = isBefore(new Date(TodayDate), new Date(NATime)) ? PreviousDate : TodayDate;
    dates =
      dateFormat(newDate, 'dd mmm yyyy') >
      dateFormat(PreRegBookingInfo?.ArrivalDate, 'dd mmm yyyy');
  }

  const handleExpandClick = (id: any) => {
    const newArray = expanded.map((f: any) => {
      if (f.SharerNo === id) {
        return { ...f, IsExpanded: !f.IsExpanded };
      }
      return { ...f };
    });
    setExpanded(newArray);
  };

  const handlePlus = (i: any) => {
    const guestRoom = guest.find((gg: any) => gg.SharerNo === i);
    const noguest = guestRoom?.ContactInfo?.length || 0;
    const maxG = expanded.find((ex: any) => ex.SharerNo === i)?.MaxGuest;
    setMaxGuest(maxG);
    if (noguest >= maxG) return;

    const result = guest.map((g: any) => {
      if (g.SharerNo === i) {
        return {
          ...g,
          ContactInfo: [
            ...g.ContactInfo,
            {
              RoomType: g.RoomType,
              Order: noguest + 1,
              Mode: 'add',
              SharerNo: g.SharerNo,
              Guest: { Contact: { Email: '', FullName: '' } },
            },
          ].sort((a: any, b: any) => a.Order - b.Order),
        };
      }
      return { ...g };
    });
    setGuest(result);
  };

  const handleMinus = (SharerNo: any) => {
    const guestRoom = guest.find((y: any) => y?.SharerNo === SharerNo);
    const ContactInfoLength: number = guestRoom?.ContactInfo?.length || 0;
    if (ContactInfoLength <= 1) return;

    const UpdatedList = guestRoom?.ContactInfo?.filter(
      (item: any) => item.Order !== ContactInfoLength
    );
    const AllGuest = guest.map((g: any) => {
      if (g.SharerNo === SharerNo) return { ...g, ContactInfo: UpdatedList };
      return { ...g };
    });
    setGuest(AllGuest);
  };

  const handleChange = useCallback(
    (event: any, fieldIndex: any, subfieldIndex: any) => {
      const newValues = [...guest];
      newValues[fieldIndex].ContactInfo[subfieldIndex].Guest.Contact = { ...event };
      setGuest(newValues);

      const inputcontact: any[] = [];
      newValues.map((m: any) => inputcontact.push(...m.ContactInfo));
      setContact(inputcontact);
    },
    [guest]
  );

  const onSubmit = () => {
    const inputEdit: any[] = [];
    const inputAdd: any[] = [];

    contact.filter((t: any) => t.ID).map((g: any) => {
      inputEdit.push({
        RegistrationID: g.ID,
        ContactID: g.Guest.Contact.ID,
        FullName: g.Guest.Contact.FullName,
        Email: g.Guest.Contact.Email,
        OldFullName: g.OldFullName,
      });
    });

    contact.filter((t: any) => t.Mode === 'add').map((g: any) => {
      inputAdd.push({
        RegistrationID: guest
          ?.find((x: any) => x.SharerNo === g.SharerNo)
          ?.ContactInfo?.find((c: any) => c.IsPrincipal)?.ID,
        FullName: g.Guest.Contact.FullName,
        Email: g.Guest.Contact.Email,
      });
    });

    ExpressCheckinConfirmation({
      variables: {
        BookingID: URLBookingID,
        HotelID: URLHotelID,
        EditConfirmationInput: inputEdit,
        AddConfirmationInput: inputAdd,
      },
    });
  };

  useEffect(() => {
    if (URLHotelID !== null && URLBookingID !== null) {
      PreRegInfo({
        variables: { HotelID: URLHotelID, BookingID: URLBookingID },
      });
    }
  }, [URLHotelID, URLBookingID]);

  useEffect(() => {
    const arr =
      PreRegBookingInfo?.result?.map((t: any) => ({
        ID: t.RoomType,
        SharerNo: t.SharerNo,
        IsExpanded: false,
        MaxGuest: t.Registrations[0]?.RoomType?.MaxGuest,
      })) ?? [];
    setExpanded(arr);
  }, [PreRegBookingInfo]);

  useEffect(() => {
    if (PreRegBookingInfo && guest.length === 0) {
      const sorted = PreRegBookingInfo?.result
        ?.slice()
        ?.sort((a, b) => a.Registrations[0]?.SharerNo - b.Registrations[0]?.SharerNo);

      const GuestArr =
        sorted?.map((t: any, i: number) => ({
          ContactInfo: t.Registrations.map((y: any, i2: number) => ({
            ...y,
            Order: i2 + 1,
            RoomType: t.RoomType,
            RoomDescription: y.RoomType?.Description,
            OldFullName: y.Guest?.Contact?.FullName,
          })),
          RoomType: t.RoomType,
          SharerNo: t.SharerNo,
        })) ?? [];

      setGuest(GuestArr);
      const ContactArr: any[] = [];
      GuestArr.map((m: any) => ContactArr.push(...m.ContactInfo));
      setContact(ContactArr);
    }
  }, [PreRegBookingInfo]);

  useEffect(() => {
    if (!URLHotelID || !URLBookingID || (!Loadings && (PreRegBookingInfo?.BookingStatus !== 'Booked' || dates === true))) {
      history('/missing-booking-info');
    }
  }, [URLHotelID, URLBookingID, Loadings, PreRegBookingInfo?.BookingStatus, dates]);

  return HotelLoadings ? (
    <Loading />
  ) : (
    <>
      <MainWrapper>
        <Box
          sx={{ p: 2 }}
          justifyContent="center"
          alignItems="center"
          width="400px"
          margin="auto"
          bgcolor="#F7FAF7"
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" mb="12px" alignItems="center" justifyContent="center">
                <img
                  src={HotelDetails?.LogoImage?.ImageURL as any}
                  alt="Hotel Logo"
                  loading="lazy"
                  width="180px"
                />
              </Box>
              <Typography fontSize="17px" fontWeight={600} mb="8px" align="center">
                Welcome To {HotelDetails?.HotelName}
              </Typography>
              <Typography fontSize="13px" fontWeight={500} mb="12px" color="black" align="center">
                Kindly complete the form below
              </Typography>
            </Grid>
          </Grid>
          <Box display="flex" height={20} mb="12px">
            <Typography fontSize="14px" fontWeight={600}>
              Booking Info
            </Typography>
          </Box>
          <Card
            sx={{
              mb: '14px',
              borderRadius: '4px',
              boxShadow:
                '0 3px 1px -2px transparent, 2px 5px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)',
            }}
          >
            <CardContent sx={{ ':last-child': { p: '10px' } }}>
              <Box width="100%" display="flex" mb="10px">
                <Box width="50%" display="flex">
                  <Typography fontSize="14px" fontWeight={500} color="black" align="left">
                    Booking No.
                  </Typography>
                </Box>
                <Box width="50%" display="flex" sx={{ flexDirection: 'row-reverse' }}>
                  <Typography fontSize="14px" fontWeight={500} color="secondary">
                    {PreRegBookingInfo?.BookingNo}
                  </Typography>
                </Box>
              </Box>
              <Box width="100%" display="flex">
                <Box width="50%" display="flex" alignItems="center">
                  <Typography fontSize="14px" fontWeight={500} color="black">
                    No of Room(s)
                  </Typography>
                </Box>
                <Box width="50%" display="flex" sx={{ flexDirection: 'row-reverse' }}>
                  <Typography fontSize="14px" fontWeight={500} color="secondary">
                    {PreRegBookingInfo?.result?.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Typography fontSize="14px" fontWeight={600} mb="10px">
            Registration Info
          </Typography>
          <List className="core-list reset-listitem">
            <div className="rm-padding p-10">
              {guest?.map((x: any, i: number) => (
                <div key={x.RoomType}>
                  <Card
                    sx={{
                      mb: '14px',
                      borderRadius: '4px',
                      boxShadow:
                        '0 3px 1px -2px transparent, 2px 5px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)',
                    }}
                  >
                    <CardActions key={x.SharerNo} disableSpacing style={{ backgroundColor: '#fff4dc' }}>
                      <Box width="100%">
                        <Box width="100%" display="flex" sx={{ margin: '5px' }}>
                          <Typography fontSize="14px" fontWeight={600} color="#f5ab35" align="left">
                            {'Room ' + (i + 1) + ' - ' + x.ContactInfo[0]?.RoomDescription}
                          </Typography>
                        </Box>
                        <Box width="100%" display="flex">
                          <Box width="100%" display="flex">
                            <Typography fontSize="14px" fontWeight={500} color="black" align="left" style={{ paddingTop: '10px' }}>
                              {dateFormat(x.ContactInfo[0]?.ArrivalDate, 'dd mmm yyyy')} -{' '}
                              {dateFormat(x.ContactInfo[0]?.DepartureDate, 'dd mmm yyyy')}
                            </Typography>
                          </Box>
                          <Box display="flex" sx={{ justifyContent: 'flex-end' }}>
                            <IconButton style={{ pointerEvents: 'none' }}>
                              <PersonAdd sx={{ fontSize: 20, color: 'black' }} />
                            </IconButton>
                            <IconButton
                              disabled={
                                x?.ContactInfo?.length ===
                                  PreRegBookingInfo?.result?.find(
                                    (o: any) => o.SharerNo === x.SharerNo
                                  )?.Registrations?.length ||
                                expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded === false
                              }
                              aria-label="remove guest"
                              onClick={() => handleMinus(x?.SharerNo)}
                              sx={{ margin: 0 }}
                            >
                              <RemoveCircle
                                sx={{
                                  fontSize: 20,
                                  color:
                                    x?.ContactInfo?.length ===
                                      PreRegBookingInfo?.result?.find(
                                        (o: any) => o.SharerNo === x.SharerNo
                                      )?.Registrations?.length ||
                                    expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded === false
                                      ? '#D3D3D3'
                                      : '#f5ab35',
                                }}
                              />
                            </IconButton>
                            <Typography fontSize="14px" fontWeight={600} color="black" marginTop={1}>
                              {x?.ContactInfo?.length}
                            </Typography>
                            <IconButton
                              aria-label="add guest"
                              onClick={() => handlePlus(x.SharerNo)}
                              disabled={
                                expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded === false
                              }
                              sx={{ margin: 0 }}
                            >
                              <AddCircle
                                sx={{
                                  fontSize: 20,
                                  color:
                                    expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded === false ||
                                    maxguest === guest[0]?.ContactInfo?.length
                                      ? '#D3D3D3'
                                      : '#f5ab35',
                                }}
                              />
                            </IconButton>
                            <ExpandMore
                              expand={expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded}
                              onClick={() => handleExpandClick(x.SharerNo)}
                              aria-expanded={expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded}
                              aria-label="show more"
                              sx={{ fontSize: 20, color: 'black', margin: '0' }}
                            >
                              <ExpandMoreIcon />
                            </ExpandMore>
                          </Box>
                        </Box>
                      </Box>
                    </CardActions>
                    <Collapse
                      key={i}
                      in={expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded}
                      timeout="auto"
                      unmountOnExit
                    >
                      <CardContent>
                        {x?.ContactInfo?.map((k: any, index2: number) => (
                          <Box key={index2} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Grid key={index2} container spacing={0}>
                              <Grid item xs={1} sx={{ pt: '12px', fontSize: '14px' }}>
                                {index2 + 1 + '. '}
                              </Grid>
                              <Grid item xs={11}>
                                <Box sx={{ display: 'flex', mb: '10px', pt: '10px' }}>
                                  <TextField
                                    required
                                    label="FullName"
                                    fullWidth
                                    inputProps={{
                                      style: { padding: '6px 0px 6px 6px', fontSize: '12px' },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    value={k?.Guest?.Contact?.FullName || ''}
                                    onChange={e => {
                                      const obj = { ...k.Guest?.Contact, FullName: e.target.value };
                                      handleChange(obj, i, index2);
                                    }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', pb: '10px' }}>
                                  <TextField
                                    required
                                    label="Email"
                                    fullWidth
                                    inputProps={{
                                      style: { padding: '6px 0px 6px 6px', fontSize: '12px' },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    value={k?.Guest?.Contact?.Email || ''}
                                    onChange={e => {
                                      const obj = { ...k.Guest?.Contact, Email: e.target.value };
                                      handleChange(obj, i, index2);
                                    }}
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </CardContent>
                    </Collapse>
                  </Card>
                </div>
              ))}
            </div>
          </List>
        </Box>
      </MainWrapper>
      <Footer
        options={[
          {
            name: 'Confirm',
            onClick: onSubmit,
          },
        ]}
      />
    </>
  );
}
